import { zodResolver } from "@hookform/resolvers/zod";
import {
  EV_COMMAND_STATUSES,
  EV_IDENTIFICATION_LABELS,
  EV_LOAD_METHOD_LABELS,
  EV_LOAD_METHODS,
  EV_OPERATION_MODEL_LABELS,
  EV_OPERATION_MODELS,
  EV_UNKNOWN_ID_TAG_POLICIES,
  EV_UNKNOWN_ID_TAG_POLICY_LABELS,
  type EvCommandStatus,
  type EvStationLoad,
  type EvStationSettingsView,
} from "@station/contracts";
import {
  Alert,
  Button,
  Card,
  EmptyState,
  FormField,
  Input,
  Select,
  SkeletonRows,
  StatusBadge,
  Textarea,
  useToast,
} from "@station/ui";
import { useMutation } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Gauge } from "lucide-react";
import { type ReactElement, type ReactNode, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import {
  formatKw,
  formatKwh,
  formatPercent,
  formatWatts,
  loadPercent,
  type SettingsFormValues,
  settingsBody,
  settingsFormFrom,
  settingsFormSchema,
} from "../../../features/ev/ev-values.js";
import { useEvLoad, useEvPickers, useEvSettings, useRefreshEv } from "../../../features/ev/queries.js";
import {
  CommandStatusBadge,
  evSearchShape,
  EvStationPage,
  type EvStationContext,
  Field,
  SessionPhaseBadge,
  TableFrame,
  Th,
} from "../../../features/ev/shared.js";
import { api } from "../../../lib/api.js";
import { errorMessage, isApiError } from "../../../lib/errors.js";
import { formatDateTime } from "../../../lib/format.js";

const searchSchema = z.object(evSearchShape);

export const Route = createFileRoute("/_app/sarj/ayarlar")({
  validateSearch: (search) => searchSchema.parse(search),
  component: SettingsRoute,
});

/**
 * İstasyon şarj ayarı ve yük yönetimi (Prompt 19 web): `GET/PUT /ev/stations/{id}/settings` — işletme modeli (+ şarj ağı
 * ortak kodu ve gizli OLMAYAN ayarları), şebeke azami gücü, yöntem (kapalı / eşit / öncelikli), emniyet payı, asgari güç,
 * öncelikler, bilinmeyen idTag politikası, EV alt sayacı; ayar TAMAMEN yazılır (`version` iyimser kilit), kayıttan sonra yük
 * yeniden paylaştırılır. `GET /ev/stations/{id}/load`: kullanılabilir güç, canlı oturumların son sınırı, sınır tarihçesi.
 * Okuma `ev.read`; kaydetme `ev.manage` (sunucunun `canEdit`'i).
 */
function SettingsRoute(): ReactElement {
  const { t } = useTranslation();
  return (
    <EvStationPage title={t("menu.evSettings")} description={t("ev.settings.description")}>
      {(context) => <SettingsBody context={context} />}
    </EvStationPage>
  );
}

function SettingsBody({ context }: { context: EvStationContext }): ReactElement {
  const settings = useEvSettings(context.station.id);
  const load = useEvLoad(context.station.id);
  if (settings.error) return <Alert tone="danger">{errorMessage(settings.error)}</Alert>;
  if (!settings.data) return <SkeletonRows rows={8} />;
  return (
    <>
      <SettingsForm key={settings.data.version} settings={settings.data} stationId={context.station.id} />
      {load.error ? (
        <Alert tone="danger">{errorMessage(load.error)}</Alert>
      ) : load.data ? (
        <LoadPanel load={load.data} />
      ) : (
        <SkeletonRows rows={4} />
      )}
    </>
  );
}

function Section({
  title,
  hint,
  children,
}: {
  title: string;
  hint?: string;
  children: ReactNode;
}): ReactElement {
  return (
    <fieldset className="grid gap-3 rounded-md border p-3">
      <legend className="px-1 text-sm font-medium">{title}</legend>
      {hint ? <p className="text-xs text-muted-foreground">{hint}</p> : null}
      {children}
    </fieldset>
  );
}

function SettingsForm({
  settings,
  stationId,
}: {
  settings: EvStationSettingsView;
  stationId: string;
}): ReactElement {
  const { t } = useTranslation();
  const toast = useToast();
  const refresh = useRefreshEv();
  const pickers = useEvPickers();
  const [formError, setFormError] = useState<string | null>(null);
  const [conflict, setConflict] = useState(false);
  const form = useForm<SettingsFormValues>({
    resolver: zodResolver(settingsFormSchema(settings.version)) as never,
    defaultValues: settingsFormFrom(settings),
  });
  const { formState, register, watch } = form;
  const values = watch();
  // Form ayar SÜRÜMÜ başına bir kez kurulur (üst bileşen `key={version}` verir): olayla gelen tazeleme yazılanları silmez.
  const meters = (pickers.data?.meters ?? []).filter((meter) => meter.stationId === stationId);
  const readOnly = !settings.canEdit;

  const save = useMutation({
    mutationFn: async (clean: SettingsFormValues) =>
      (
        await api.PUT("/ev/stations/{stationId}/settings", {
          params: { path: { stationId } },
          body: settingsBody(clean, settings.version) as never,
        })
      ).data!,
    onSuccess: async () => {
      await refresh();
      toast({ tone: "success", title: t("ev.settings.saved") });
    },
    onError: async (caught) => {
      if (isApiError(caught, "VERSION_CONFLICT")) {
        setConflict(true);
        await refresh();
        return;
      }
      let matched = 0;
      if (isApiError(caught))
        for (const fieldError of caught.problem.errors ?? []) {
          const path = fieldError.path.replace(/^body\./, "");
          const field = path.startsWith("partnerConfig") ? "partnerConfigText" : path;
          form.setError(field as never, { type: "server", message: fieldError.message });
          matched += 1;
        }
      if (matched === 0) setFormError(errorMessage(caught));
    },
  });

  const submit = form.handleSubmit((clean) => {
    setFormError(null);
    setConflict(false);
    save.mutate(clean as SettingsFormValues);
  });
  const partner = values.operationModel === "NETWORK_PARTNER";
  const loadOn = values.loadMethod !== "OFF";

  return (
    <Card className="grid gap-4 p-4">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-base font-semibold">{t("ev.settings.formTitle")}</h2>
        <span className="text-xs text-muted-foreground">
          {settings.isDefault
            ? t("ev.settings.defaults")
            : t("ev.settings.updatedAt", { at: formatDateTime(settings.updatedAt) })}
        </span>
      </div>
      {readOnly ? <Alert tone="info">{t("ev.settings.readOnly")}</Alert> : null}
      {conflict ? <Alert tone="warning">{t("ev.conflict")}</Alert> : null}
      {formError ? <Alert tone="danger">{formError}</Alert> : null}
      <form
        className="grid gap-4"
        noValidate
        aria-label={t("ev.settings.formTitle")}
        onSubmit={(event) => void submit(event)}
      >
        <fieldset className="grid gap-4" disabled={readOnly || save.isPending}>
          <Section title={t("ev.settings.sections.operation")} hint={t("ev.settings.operationHint")}>
            <div className="grid items-start gap-4 sm:grid-cols-2">
              <FormField label={t("ev.settings.fields.operationModel")} required>
                {(control) => (
                  <Select {...control} {...register("operationModel")}>
                    {EV_OPERATION_MODELS.map((model) => (
                      <option key={model} value={model}>
                        {EV_OPERATION_MODEL_LABELS[model]}
                      </option>
                    ))}
                  </Select>
                )}
              </FormField>
              <FormField
                label={t("ev.settings.fields.unknownIdTagPolicy")}
                description={t("ev.settings.unknownIdTagHint")}
                required
              >
                {(control) => (
                  <Select {...control} {...register("unknownIdTagPolicy")}>
                    {EV_UNKNOWN_ID_TAG_POLICIES.map((policy) => (
                      <option key={policy} value={policy}>
                        {EV_UNKNOWN_ID_TAG_POLICY_LABELS[policy]}
                      </option>
                    ))}
                  </Select>
                )}
              </FormField>
            </div>
            {partner ? (
              <div className="grid items-start gap-4 sm:grid-cols-2">
                <FormField
                  label={t("ev.settings.fields.partnerCode")}
                  description={t("ev.settings.partnerCodeHint")}
                  error={formState.errors.partnerCode?.message}
                  required
                >
                  {(control) => (
                    <Input
                      {...control}
                      {...register("partnerCode")}
                      className="font-mono uppercase"
                      maxLength={40}
                      autoComplete="off"
                    />
                  )}
                </FormField>
                <FormField
                  label={t("ev.settings.fields.partnerStationRef")}
                  error={formState.errors.partnerStationRef?.message}
                >
                  {(control) => (
                    <Input
                      {...control}
                      {...register("partnerStationRef")}
                      maxLength={100}
                      autoComplete="off"
                    />
                  )}
                </FormField>
                <FormField
                  className="sm:col-span-2"
                  label={t("ev.settings.fields.partnerConfig")}
                  description={t("ev.settings.partnerConfigHint")}
                  error={formState.errors.partnerConfigText?.message}
                >
                  {(control) => (
                    <Textarea
                      {...control}
                      {...register("partnerConfigText")}
                      rows={3}
                      className="font-mono"
                    />
                  )}
                </FormField>
              </div>
            ) : null}
          </Section>

          <Section title={t("ev.settings.sections.load")} hint={t("ev.settings.loadHint")}>
            <div className="grid items-start gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <FormField label={t("ev.settings.fields.loadMethod")} required>
                {(control) => (
                  <Select {...control} {...register("loadMethod")}>
                    {EV_LOAD_METHODS.map((method) => (
                      <option key={method} value={method}>
                        {EV_LOAD_METHOD_LABELS[method]}
                      </option>
                    ))}
                  </Select>
                )}
              </FormField>
              <FormField
                label={t("ev.settings.fields.maxGridKw")}
                description={t("ev.settings.maxGridHint")}
                error={formState.errors.maxGridKw?.message}
                required={loadOn}
              >
                {(control) => (
                  <Input {...control} {...register("maxGridKw")} inputMode="decimal" className="tabular" />
                )}
              </FormField>
              <FormField
                label={t("ev.settings.fields.safetyMargin")}
                description={t("ev.settings.safetyMarginHint")}
                error={formState.errors.safetyMarginPercent?.message}
                required
              >
                {(control) => (
                  <Input
                    {...control}
                    {...register("safetyMarginPercent")}
                    inputMode="decimal"
                    className="tabular"
                  />
                )}
              </FormField>
              <FormField
                label={t("ev.settings.fields.minSessionPower")}
                description={t("ev.settings.minSessionPowerHint")}
                error={formState.errors.minSessionPowerKw?.message}
                required
              >
                {(control) => (
                  <Input
                    {...control}
                    {...register("minSessionPowerKw")}
                    inputMode="decimal"
                    className="tabular"
                  />
                )}
              </FormField>
            </div>
            {values.loadMethod === "PRIORITY" ? (
              <div className="grid items-start gap-4 sm:grid-cols-3">
                <p className="text-xs text-muted-foreground sm:col-span-3">{t("ev.settings.priorityHint")}</p>
                <FormField
                  label={t("ev.settings.fields.priorityFleet")}
                  error={formState.errors.priorityFleet?.message}
                >
                  {(control) => (
                    <Input
                      {...control}
                      {...register("priorityFleet")}
                      inputMode="numeric"
                      className="tabular"
                    />
                  )}
                </FormField>
                <FormField
                  label={t("ev.settings.fields.prioritySubscription")}
                  error={formState.errors.prioritySubscription?.message}
                >
                  {(control) => (
                    <Input
                      {...control}
                      {...register("prioritySubscription")}
                      inputMode="numeric"
                      className="tabular"
                    />
                  )}
                </FormField>
                <FormField
                  label={t("ev.settings.fields.priorityApp")}
                  error={formState.errors.priorityApp?.message}
                >
                  {(control) => (
                    <Input
                      {...control}
                      {...register("priorityApp")}
                      inputMode="numeric"
                      className="tabular"
                    />
                  )}
                </FormField>
              </div>
            ) : null}
          </Section>

          <Section title={t("ev.settings.sections.cost")} hint={t("ev.settings.meterHint")}>
            <FormField
              label={t("ev.settings.fields.electricityMeter")}
              className="sm:max-w-md"
              error={formState.errors.electricityMeterId?.message}
            >
              {(control) => (
                <Select {...control} {...register("electricityMeterId")}>
                  <option value="">{t("ev.settings.noMeter")}</option>
                  {settings.electricityMeterId &&
                  !meters.some((meter) => meter.id === settings.electricityMeterId) ? (
                    <option value={settings.electricityMeterId}>
                      {settings.electricityMeterName ?? settings.electricityMeterId}
                    </option>
                  ) : null}
                  {meters.map((meter) => (
                    <option key={meter.id} value={meter.id}>
                      {meter.name}
                      {meter.businessUnitType === "EV" ? ` (${t("ev.settings.evMeter")})` : ""}
                    </option>
                  ))}
                </Select>
              )}
            </FormField>
          </Section>
        </fieldset>
        {readOnly ? null : (
          <div className="flex justify-end">
            <Button type="submit" loading={save.isPending}>
              {t("common.save")}
            </Button>
          </div>
        )}
      </form>
    </Card>
  );
}

function LoadPanel({ load }: { load: EvStationLoad }): ReactElement {
  const { t } = useTranslation();
  const percent = loadPercent(load.drawingW, load.availableW);
  return (
    <Card className="grid min-w-0 gap-4 p-4 [&>*]:min-w-0" data-testid="ev-load">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-base font-semibold">{t("ev.load.title")}</h2>
        <StatusBadge tone={load.loadMethod === "OFF" ? "neutral" : "info"}>
          {EV_LOAD_METHOD_LABELS[load.loadMethod]}
        </StatusBadge>
      </div>
      <dl className="grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-4">
        <Field label={t("ev.load.maxGrid")}>
          <span className="tabular">{formatKw(load.maxGridKw)}</span>
        </Field>
        <Field label={t("ev.load.safetyMargin")}>
          <span className="tabular">{formatPercent(load.safetyMarginPercent, 0)}</span>
        </Field>
        <Field label={t("ev.load.available")}>
          <span className="tabular">
            {load.availableW === null ? t("ev.load.noLimit") : formatWatts(load.availableW)}
          </span>
        </Field>
        <Field label={t("ev.load.drawing")}>
          <span className="tabular">{formatWatts(load.drawingW)}</span>
        </Field>
      </dl>
      {percent !== null ? (
        <div className="grid gap-1">
          <div
            className="h-3 overflow-hidden rounded-full bg-muted"
            role="meter"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={Number(percent)}
            aria-label={t("ev.load.usage")}
            aria-valuetext={t("ev.load.usageText", { percent: formatPercent(percent) })}
          >
            <div className="h-full bg-info" style={{ width: `${percent}%` }} />
          </div>
          <span className="text-xs text-muted-foreground">
            {t("ev.load.usageText", { percent: formatPercent(percent) })}
          </span>
        </div>
      ) : null}

      <div className="grid min-w-0 gap-2 [&>*]:min-w-0">
        <h3 className="text-sm font-semibold">{t("ev.load.liveTitle")}</h3>
        {load.liveSessions.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t("ev.load.noLive")}</p>
        ) : (
          <TableFrame label={t("ev.load.liveTitle")}>
            <thead className="bg-muted/60 text-xs uppercase text-muted-foreground">
              <tr>
                <Th>{t("ev.fields.charger")}</Th>
                <Th>{t("ev.fields.phase")}</Th>
                <Th>{t("ev.fields.identification")}</Th>
                <Th align="right">{t("ev.fields.energy")}</Th>
                <Th align="right">{t("ev.fields.power")}</Th>
                <Th align="right">{t("ev.fields.limit")}</Th>
              </tr>
            </thead>
            <tbody>
              {load.liveSessions.map((session) => (
                <tr key={session.sessionId} className="border-t">
                  <th scope="row" className="px-3 py-2 text-left font-medium">
                    {session.chargerName} · {t("ev.connector.no", { no: session.connectorNo })}
                    <span className="block text-xs font-normal text-muted-foreground">
                      {formatKw(session.connectorPowerKw)}
                    </span>
                  </th>
                  <td className="px-3 py-2">
                    <SessionPhaseBadge phase={session.phase} />
                  </td>
                  <td className="px-3 py-2">{EV_IDENTIFICATION_LABELS[session.identification]}</td>
                  <td className="px-3 py-2 text-right tabular">{formatKwh(session.energyKwh)}</td>
                  <td className="px-3 py-2 text-right tabular">{formatWatts(session.powerW)}</td>
                  <td className="px-3 py-2 text-right tabular whitespace-nowrap">
                    {session.suspended ? (
                      <StatusBadge tone="warning">{t("ev.load.suspended")}</StatusBadge>
                    ) : session.currentLimitW === null ? (
                      t("ev.load.unlimited")
                    ) : (
                      formatWatts(session.currentLimitW)
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </TableFrame>
        )}
      </div>

      <div className="grid min-w-0 gap-2 [&>*]:min-w-0">
        <h3 className="text-sm font-semibold">{t("ev.load.historyTitle")}</h3>
        {load.history.length === 0 ? (
          <EmptyState
            headingLevel={3}
            icon={<Gauge />}
            title={t("ev.load.noHistoryTitle")}
            description={t("ev.load.noHistoryDescription")}
          />
        ) : (
          <TableFrame label={t("ev.load.historyTitle")}>
            <thead className="bg-muted/60 text-xs uppercase text-muted-foreground">
              <tr>
                <Th>{t("ev.sessionDetail.time")}</Th>
                <Th>{t("ev.fields.charger")}</Th>
                <Th>{t("ev.load.method")}</Th>
                <Th align="right">{t("ev.load.available")}</Th>
                <Th align="right">{t("ev.fields.limit")}</Th>
                <Th>{t("ev.load.reasonLabel")}</Th>
                <Th>{t("ev.load.command")}</Th>
              </tr>
            </thead>
            <tbody>
              {load.history.map((row) => (
                <tr key={row.id} className="border-t">
                  <td className="px-3 py-2 tabular whitespace-nowrap">{formatDateTime(row.at)}</td>
                  <td className="px-3 py-2">
                    {row.chargerName} · {t("ev.connector.no", { no: row.connectorNo })}
                  </td>
                  <td className="px-3 py-2">{t(`ev.loadMethod.${row.method}`)}</td>
                  <td className="px-3 py-2 text-right tabular">{formatWatts(row.availableW)}</td>
                  <td className="px-3 py-2 text-right tabular whitespace-nowrap">
                    {row.previousLimitW !== null ? `${formatWatts(row.previousLimitW)} → ` : ""}
                    {row.suspended ? t("ev.load.suspended") : formatWatts(row.limitW)}
                  </td>
                  <td className="px-3 py-2">{t(`ev.limitReason.${row.reason}`)}</td>
                  <td className="px-3 py-2">
                    {row.commandStatus &&
                    (EV_COMMAND_STATUSES as readonly string[]).includes(row.commandStatus) ? (
                      <CommandStatusBadge status={row.commandStatus as EvCommandStatus} />
                    ) : (
                      (row.commandStatus ?? "—")
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </TableFrame>
        )}
      </div>
    </Card>
  );
}
